import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
} from '@tanstack/react-router';
import { NotFoundPage, RouteErrorPage } from './components/RouterErrorPage';

function Root() {
  return <Outlet />;
}

const rootRoute = createRootRoute({ component: Root });
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: lazyRouteComponent(() => import('./pages/DashboardPage'), 'DashboardPage'),
});
const sensitiveWordsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sensitive-words',
  component: lazyRouteComponent(() => import('./pages/SensitiveWordsPage'), 'SensitiveWordsPage'),
});
const overviewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/overview',
  component: lazyRouteComponent(() => import('./pages/OverviewPage'), 'OverviewPage'),
});
const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: lazyRouteComponent(() => import('./pages/AnalyticsPage'), 'AnalyticsPage'),
});
const domSelectorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dom-selector',
  component: lazyRouteComponent(() => import('./pages/DomSelectorPage'), 'DomSelectorPage'),
});
const pluginVersionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plugin-versions',
  component: lazyRouteComponent(() => import('./pages/PluginVersionsPage'), 'PluginVersionsPage'),
});
const promptsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/prompts',
  component: lazyRouteComponent(() => import('./pages/PromptTemplatesPage'), 'PromptTemplatesPage'),
});
const skillsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/skills',
  component: lazyRouteComponent(() => import('./pages/SkillsPage'), 'SkillsPage'),
});
const chatConfigRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/chat-config',
  component: lazyRouteComponent(() => import('./pages/ChatConfigPage'), 'ChatConfigPage'),
});
const plansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plans',
  component: lazyRouteComponent(() => import('./pages/PlansPage'), 'PlansPage'),
});
const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  component: lazyRouteComponent(() => import('./pages/BillingLedgerPage'), 'BillingLedgerPage'),
});
const modelsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/models',
  component: lazyRouteComponent(() => import('./pages/ModelRoutingPage'), 'ModelRoutingPage'),
});
const modelRoutingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/model-routing',
  component: lazyRouteComponent(() => import('./pages/ModelRoutingPage'), 'ModelRoutesPage'),
});
const aiFeaturesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/ai-features',
  component: lazyRouteComponent(() => import('./pages/ModelRoutingPage'), 'AiFeaturesPage'),
});
const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: lazyRouteComponent(() => import('./pages/SettingsPage'), 'SettingsPage'),
});
const healthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/health',
  component: lazyRouteComponent(() => import('./pages/HealthPage'), 'HealthPage'),
});
const usersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/users',
  component: lazyRouteComponent(() => import('./pages/UsersPage'), 'UsersPage'),
});
const queueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/queue',
  component: lazyRouteComponent(() => import('./pages/QueuePage'), 'QueuePage'),
});
const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/audit',
  component: lazyRouteComponent(() => import('./pages/AuditPage'), 'AuditPage'),
});
const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/notifications',
  component: lazyRouteComponent(() => import('./pages/NotificationsPage'), 'NotificationsPage'),
});
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: lazyRouteComponent(() => import('./pages/LoginPage'), 'LoginPage'),
});
const securitySetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/security-setup',
  component: lazyRouteComponent(() => import('./pages/SecuritySetupPage'), 'SecuritySetupPage'),
});

export const router = createRouter({
  defaultErrorComponent: RouteErrorPage,
  defaultNotFoundComponent: NotFoundPage,
  defaultPreload: 'intent',
  routeTree: rootRoute.addChildren([
    loginRoute,
    securitySetupRoute,
    indexRoute,
    sensitiveWordsRoute,
    overviewRoute,
    analyticsRoute,
    domSelectorRoute,
    pluginVersionsRoute,
    promptsRoute,
    skillsRoute,
    chatConfigRoute,
    plansRoute,
    billingRoute,
    modelsRoute,
    modelRoutingRoute,
    aiFeaturesRoute,
    settingsRoute,
    healthRoute,
    usersRoute,
    queueRoute,
    auditRoute,
    notificationsRoute,
  ]),
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
